// This file is automatically generated. Do not edit it directly.
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

// supabase-js v2 auto-initialises on import: it reads
// `sb-<projectRef>-auth-token` from localStorage and, if present + expired,
// POSTs to /auth/v1/token?grant_type=refresh_token. If the refresh token has
// already been used (one-shot tokens) or revoked, the response is 401 — and
// the browser logs that to the console for every visitor whose storage still
// holds the dead session, even though the SDK clears it on failure.
//
// Drop sessions that are demonstrably unrecoverable BEFORE the SDK runs its
// init. This is intentionally narrow: only malformed JSON, or sessions with
// an expired access_token AND no refresh_token, are wiped. Live, refreshable
// sessions are left alone so logged-in users continue to refresh transparently.
function pruneUnrecoverableAuthSession() {
  if (typeof window === "undefined" || !window.localStorage) return;
  try {
    const projectRef = SUPABASE_URL?.match(/^https?:\/\/([^.]+)\./)?.[1];
    if (!projectRef) return;
    const key = `sb-${projectRef}-auth-token`;
    const raw = window.localStorage.getItem(key);
    if (!raw) return;
    let parsed: { expires_at?: number; refresh_token?: string } | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      window.localStorage.removeItem(key);
      return;
    }
    if (!parsed) {
      window.localStorage.removeItem(key);
      return;
    }
    const nowSec = Math.floor(Date.now() / 1000);
    const expired =
      typeof parsed.expires_at === "number" && parsed.expires_at < nowSec;
    const refreshable =
      typeof parsed.refresh_token === "string" && parsed.refresh_token.length > 0;
    if (expired && !refreshable) {
      window.localStorage.removeItem(key);
    }
  } catch {
    /* never throw at module load */
  }
}

pruneUnrecoverableAuthSession();

export const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
  auth: {
    storage: localStorage,
    persistSession: true,
    autoRefreshToken: true,
  }
});